"use client";

/**
 * Unified transaction hook for swap operations.
 *
 * Routes to widget hooks (BridgeWalletAdapter) when WidgetWalletProvider is mounted,
 * otherwise falls back to Para hooks for the main site.
 *
 * useHandleSwap and other swap components should use this instead of
 * useParaTransactions directly to support both main site and widget contexts.
 */

import { useCallback } from "react";
import { VersionedTransaction, Transaction } from "@solana/web3.js";
import { useWidgetWalletContext } from "@/contexts/widget-wallet-context";
import { useParaTransactions } from "@/hooks/use-para-transactions";

/** Return type matches useParaTransactions exactly */
export interface SwapTransactionsResult {
  onSendTransaction: (
    transaction: VersionedTransaction | Transaction
  ) => Promise<string>;
}

export function useSwapTransactions(): SwapTransactionsResult {
  // Check if we're in widget context
  const widgetContext = useWidgetWalletContext();

  // Always call Para hook (React hook rules - can't conditionally call)
  const paraTransactions = useParaTransactions();

  const onSendTransaction = useCallback(
    async (transaction: VersionedTransaction | Transaction): Promise<string> => {
      // Widget context takes priority when mounted
      if (widgetContext) {
        return widgetContext.onSendTransaction(transaction);
      }

      // Fall back to Para hooks on main site
      return paraTransactions.onSendTransaction(transaction);
    },
    [widgetContext, paraTransactions]
  );

  return { onSendTransaction };
}
